import { useEffect, useRef, useState } from "react";
import { Download } from "lucide-react";
import { Button } from "@/design-system/primitives";
import { useLabPdf } from "@/lib/api/labHooks";
import { useAuth } from "@/lib/auth/AuthProvider";
import { labReportUrl } from "./labPresentation";
import { useLabEvent } from "./labExperiment";

export function LabPdfDownload({
  orderId,
  resultId,
  reportIndex = 0,
  clinician = false,
  adminPatientId,
}: {
  orderId: string;
  resultId: string;
  reportIndex?: number;
  clinician?: boolean;
  adminPatientId?: string;
}) {
  const pdf = useLabPdf(
    orderId,
    resultId,
    reportIndex,
    clinician,
    adminPatientId,
  );
  const { session } = useAuth();
  const owner = useRef(session?.user.id);
  owner.current = session?.user.id;
  const alive = useRef(true);
  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);
  const [failed, setFailed] = useState(false);
  const emit = useLabEvent();
  return (
    <div className="flex flex-col items-start gap-8">
      <Button
        disabled={pdf.isPending}
        onClick={async () => {
          const requestedOwner = owner.current;
          setFailed(false);
          try {
            const report = await pdf.mutateAsync();
            if (
              !alive.current ||
              !requestedOwner ||
              owner.current !== requestedOwner
            )
              return;
            const url = labReportUrl(report.download_url);
            if (!url || report.content_type !== "application/pdf")
              throw new Error("PDF unavailable");
            const link = document.createElement("a");
            link.href = url;
            link.className = "ph-no-capture";
            link.download = "System-lab-report.pdf";
            link.target = "_blank";
            link.rel = "noopener noreferrer";
            document.body.appendChild(link);
            link.click();
            link.remove();
            if (!clinician && !adminPatientId)
              emit("blood_test_report_downloaded", { source: "lab_results" });
          } catch {
            if (alive.current && owner.current === requestedOwner)
              setFailed(true);
          }
        }}
      >
        <Download size={16} aria-hidden />
        {pdf.isPending ? "Preparing PDF…" : "Download PDF"}
      </Button>
      {failed && (
        <p role="alert" className="type-body-sm text-danger max-w-[28rem]">
          We couldn’t prepare your PDF. Try again.
        </p>
      )}
    </div>
  );
}
